(function () {
  "use strict";

  const config = window.prescriptionCartGuardConfig;
  if (!config || !Number(config.feeVariantId)) return;

  const requestPropertyPrefix = String(
    config.requestPropertyPrefix || "Prescription will be issued",
  ).toLowerCase();
  const feeHandle = String(config.feeHandle || "prescription-fee");

  function hasMeaningfulValue(value) {
    if (value === null || typeof value === "undefined") return false;
    const normalized = String(value).trim().toLowerCase();
    return normalized !== "" && normalized !== "false" && normalized !== "no";
  }

  function requestsNewPrescription(item) {
    return Object.entries(item.properties || {}).some(
      ([key, value]) =>
        String(key).trim().toLowerCase().startsWith(requestPropertyPrefix) &&
        hasMeaningfulValue(value),
    );
  }

  function analyzeCart(cart) {
    const requestedProducts = new Map();
    const feeLines = [];
    const variantToProductId = new Map();

    for (const item of cart.items || []) {
      if (item.handle === feeHandle) {
        feeLines.push(item);
        continue;
      }

      if (Number(item.quantity) > 0) {
        variantToProductId.set(String(item.variant_id), String(item.product_id));
      }

      if (!requestsNewPrescription(item)) continue;

      const productId = String(item.product_id);
      if (!requestedProducts.has(productId)) {
        requestedProducts.set(productId, item);
      }
    }

    const expectedFeeCount = requestedProducts.size;
    const actualFeeCount = feeLines.reduce(
      (total, item) => total + Math.max(0, Number(item.quantity) || 0),
      0,
    );
    const orphanFeeLines = feeLines.filter((line) => {
      const linkedVariantId = String((line.properties || {}).__extra || "");
      return linkedVariantId !== "" && !variantToProductId.has(linkedVariantId);
    });
    const linkedFeeUpdates = Object.fromEntries(
      orphanFeeLines.map((line) => [line.key, 0]),
    );
    const productsWithRetainedFee = new Set();

    for (const line of feeLines) {
      const linkedVariantId = String((line.properties || {}).__extra || "");
      const linkedProductId = variantToProductId.get(linkedVariantId);
      if (!linkedProductId) continue;

      const quantity = Math.max(0, Number(line.quantity) || 0);
      if (!productsWithRetainedFee.has(linkedProductId) && quantity > 0) {
        productsWithRetainedFee.add(linkedProductId);
        if (quantity > 1) linkedFeeUpdates[line.key] = 1;
      } else if (quantity > 0) {
        linkedFeeUpdates[line.key] = 0;
      }
    }

    return {
      requestedProducts: Array.from(requestedProducts.values()),
      feeLines,
      orphanFeeLines,
      linkedFeeUpdates,
      expectedFeeCount,
      actualFeeCount,
      isValid: expectedFeeCount === actualFeeCount,
    };
  }

  async function fetchJson(url, options) {
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(options && options.body
          ? { "Content-Type": "application/json" }
          : {}),
      },
      ...options,
    });

    const responseBody = await response.json();
    if (!response.ok || responseBody.status >= 400 || responseBody.errors) {
      throw new Error(responseBody.description || responseBody.message || "Cart update failed");
    }
    return responseBody;
  }

  function cartJsonUrl() {
    return `${window.routes.cart_url}.js`;
  }

  async function getCart() {
    return fetchJson(cartJsonUrl());
  }

  function getSectionsToRender() {
    const sections = [];
    const cartIcon = document.getElementById("Cart-Icon-Bubble");
    const bottomCartIcon = document.getElementById("Bottom-Cart-Bubble");
    const drawer = document.getElementById("Cart-Drawer");
    const mainCart = document.getElementById("Main-Cart");

    if (cartIcon) {
      sections.push({
        id: "Cart-Icon-Bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      });
    }
    if (bottomCartIcon) {
      sections.push({
        id: "Bottom-Cart-Bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      });
    }
    if (drawer && drawer.dataset.section) {
      sections.push(
        {
          id: "Cart-Drawer",
          section: drawer.dataset.section,
          selector: "#Cart-Drawer-Details",
        },
        {
          id: "Cart-Drawer",
          section: drawer.dataset.section,
          selector: ".cart-count",
        },
      );
    }
    if (mainCart && mainCart.dataset.section) {
      sections.push(
        {
          id: "Main-Cart",
          section: mainCart.dataset.section,
          selector: "#Main-Cart-Details",
        },
        {
          id: "Main-Cart",
          section: mainCart.dataset.section,
          selector: ".cart-count",
        },
      );
    }

    return sections;
  }

  async function updateFeeLines(updates, refreshVisibleCart) {
    if (Object.keys(updates).length === 0) return;

    const sectionsToRender = refreshVisibleCart ? getSectionsToRender() : [];
    const body = { updates };

    if (sectionsToRender.length > 0) {
      body.sections = [...new Set(sectionsToRender.map((item) => item.section))];
      body.sections_url = window.location.pathname;
    }

    const response = await fetchJson(window.routes.cart_update_url, {
      method: "POST",
      body: JSON.stringify(body),
    });

    if (
      refreshVisibleCart &&
      response.sections &&
      typeof SectionDynamicUpdate !== "undefined"
    ) {
      SectionDynamicUpdate.updateSections(sectionsToRender, response.sections);
    }
  }

  async function normalizeLinkedFees(cart, refreshVisibleCart) {
    const analysis = analyzeCart(cart);
    if (Object.keys(analysis.linkedFeeUpdates).length === 0) return analysis;

    await updateFeeLines(analysis.linkedFeeUpdates, refreshVisibleCart);
    return analyzeCart(await getCart());
  }

  async function addMissingFees(analysis) {
    let missingCount = analysis.expectedFeeCount - analysis.actualFeeCount;
    if (missingCount <= 0) return;

    const linkedVariantIds = new Set(
      analysis.feeLines
        .filter((line) => Number(line.quantity) > 0)
        .map((line) => String((line.properties || {}).__extra || ""))
        .filter(Boolean),
    );

    const unlinkedRequests = analysis.requestedProducts.filter(
      (item) => !linkedVariantIds.has(String(item.variant_id)),
    );
    const candidates = [
      ...unlinkedRequests,
      ...analysis.requestedProducts.filter((item) =>
        linkedVariantIds.has(String(item.variant_id)),
      ),
    ];

    const items = [];
    for (const item of candidates) {
      if (missingCount <= 0) break;
      items.push({
        id: Number(config.feeVariantId),
        quantity: 1,
        properties: { __extra: String(item.variant_id) },
      });
      missingCount -= 1;
    }

    if (items.length === 0 || missingCount > 0) {
      throw new Error("Unable to identify the required prescription fee items");
    }

    await fetchJson(window.routes.cart_add_url, {
      method: "POST",
      body: JSON.stringify({ items }),
    });
  }

  async function removeExcessFees(analysis) {
    let excessCount = analysis.actualFeeCount - analysis.expectedFeeCount;
    if (excessCount <= 0) return;

    const updates = {};
    for (const line of [...analysis.feeLines].reverse()) {
      if (excessCount <= 0) break;
      const currentQuantity = Math.max(0, Number(line.quantity) || 0);
      const reduction = Math.min(currentQuantity, excessCount);
      updates[line.key] = currentQuantity - reduction;
      excessCount -= reduction;
    }

    if (excessCount > 0) throw new Error("Unable to correct prescription fee quantity");

    await fetchJson(window.routes.cart_update_url, {
      method: "POST",
      body: JSON.stringify({ updates }),
    });
  }

  async function reconcileCart(cart) {
    const analysisAfterRelationshipCleanup = await normalizeLinkedFees(cart, false);
    const analysis = analysisAfterRelationshipCleanup;
    if (analysis.isValid) return analysis;

    if (analysis.actualFeeCount < analysis.expectedFeeCount) {
      await addMissingFees(analysis);
    } else {
      await removeExcessFees(analysis);
    }

    return analyzeCart(await getCart());
  }

  function checkoutUrl() {
    const rootUrl = String(window.routes.root_url || "/").replace(/\/?$/, "/");
    return `${rootUrl}checkout`;
  }

  function setCheckoutBusy(isBusy) {
    document.querySelectorAll('[name="checkout"]').forEach((button) => {
      button.disabled = isBusy;
      button.setAttribute("aria-busy", String(isBusy));
    });
  }

  function showError() {
    const message =
      config.errorMessage ||
      "We could not verify the required prescription fee. Please review your cart and try again.";

    if (typeof webvista !== "undefined" && typeof webvista.popToast === "function") {
      webvista.popToast(message, "error");
    } else {
      window.alert(message);
    }
  }

  let checkoutInProgress = false;
  let cartOperationQueue = Promise.resolve();
  let orphanCleanupTimer;

  function serializeCartOperation(operation) {
    const result = cartOperationQueue.then(operation, operation);
    cartOperationQueue = result.catch(() => {});
    return result;
  }

  function scheduleOrphanCleanup() {
    window.clearTimeout(orphanCleanupTimer);
    orphanCleanupTimer = window.setTimeout(() => {
      serializeCartOperation(async () => {
        try {
          await normalizeLinkedFees(await getCart(), true);
        } catch (error) {
          console.error("Prescription fee relationship cleanup failed", error);
        }
      });
    }, Number(config.cleanupDelay) || 600);
  }

  async function validateAndCheckout() {
    if (checkoutInProgress) return;
    checkoutInProgress = true;
    setCheckoutBusy(true);

    try {
      window.clearTimeout(orphanCleanupTimer);
      const finalAnalysis = await serializeCartOperation(() =>
        getCart().then(reconcileCart),
      );
      if (!finalAnalysis.isValid) throw new Error("Prescription fee count is invalid");
      window.location.assign(checkoutUrl());
    } catch (error) {
      console.error("Prescription cart validation failed", error);
      showError();
      checkoutInProgress = false;
      setCheckoutBusy(false);
    }
  }

  document.addEventListener(
    "submit",
    (event) => {
      if (!event.submitter || event.submitter.name !== "checkout") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      validateAndCheckout();
    },
    true,
  );

  document.addEventListener(
    "click",
    (event) => {
      const checkoutLink = event.target.closest('a[href]');
      if (!checkoutLink) return;

      const url = new URL(checkoutLink.href, window.location.origin);
      if (!url.pathname.replace(/\/+$/, "").endsWith("/checkout")) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      validateAndCheckout();
    },
    true,
  );

  if (
    typeof webvista !== "undefined" &&
    typeof webvista.subscribe === "function" &&
    typeof PUB_SUB_EVENTS !== "undefined"
  ) {
    webvista.subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleOrphanCleanup);
  }

  window.addEventListener("pageshow", scheduleOrphanCleanup);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") scheduleOrphanCleanup();
  });

  window.prescriptionCartGuard = {
    analyzeCart,
    reconcileCart,
    normalizeLinkedFees,
    removeOrphanedFees: normalizeLinkedFees,
    scheduleOrphanCleanup,
  };
})();
